/**
 * The compute implementation behind the worker protocol.
 *
 * Pure orchestration over @sae/io, @sae/automation, and @sae/engine:
 * every number comes from the validated engine (brief P2), every
 * recode or collapse is an explicit automation transformation the
 * user confirmed (P3), and exclusions are named, never silent (P4).
 */

import {
  assessTable,
  detectPreset,
  proposeCollapse,
  proposeIndicatorRule,
  previewFeasibility,
  runQcChecks,
} from '@sae/automation';
import type { CollapseRule, IndicatorRule } from '@sae/automation';
import { runAnalysis } from '@sae/engine';
import { readTable, tableToAreaLevelCell } from '@sae/io';
import type { AreaLevelRoles } from '@sae/io';

import { disclosureConfigFor, resolveTransform } from '../domain/defaults.js';
import type { ComputeApi, PipelineJob } from '../worker/protocol.js';
import { previewAreaLevel, runAreaLevel } from './areaLevel.js';
import { prepareMicrodataRun } from './buildInputs.js';

function areaLevelRolesOf(job: PipelineJob): AreaLevelRoles {
  const roles = job.roles;
  if (
    roles.areaId === undefined ||
    roles.estimate === undefined ||
    roles.sampleSize === undefined
  ) {
    throw new Error(
      'An area column, a direct estimate column, and a sample size column must be confirmed before this step.',
    );
  }
  return {
    area: roles.areaId,
    estimate: roles.estimate,
    ...(roles.variance !== undefined ? { variance: roles.variance } : {}),
    ...(roles.standardError !== undefined
      ? { standardError: roles.standardError }
      : {}),
    sampleSize: roles.sampleSize,
    ...(roles.populationCount !== undefined
      ? { populationCount: roles.populationCount }
      : {}),
    covariates: roles.covariates,
    indicatorId: job.areaIndicatorId || 'indicator',
  };
}

export function createComputeHandler(): ComputeApi {
  return {
    async parseFile(bytes, filename) {
      return readTable({ bytes, filename });
    },

    async assess(table) {
      const assessment = assessTable(table);
      const indicatorRules: Record<string, IndicatorRule> = {};
      for (const column of assessment.roles.roles.indicators) {
        indicatorRules[column] = proposeIndicatorRule(
          table,
          column,
          assessment.preset,
        );
      }
      const collapseProposals: Record<string, CollapseRule | null> = {};
      for (const variable of assessment.roles.roles.subgroups) {
        collapseProposals[variable] = proposeCollapse(table, variable);
      }
      return { assessment, indicatorRules, collapseProposals };
    },

    async runQc(table, profile, context) {
      return runQcChecks(table, profile, context);
    },

    async proposeIndicatorRule(table, column) {
      const { preset } = detectPreset(table);
      return proposeIndicatorRule(table, column, preset);
    },

    async previewFeasibility(job) {
      const disclosure = disclosureConfigFor(job.runConfig.disclosurePresetId);
      if (job.path === 'area-level') {
        const conversion = tableToAreaLevelCell(
          job.table,
          areaLevelRolesOf(job),
          { estimateScale: job.estimateScale },
        );
        if (conversion.cell === null) {
          const first = conversion.issues.find((i) => i.severity === 'error');
          return {
            ok: false,
            blockedReason:
              first?.message ?? 'The area-level table could not be converted.',
            log: [],
            totalCells: 0,
            fittedCells: 0,
            warningCells: 0,
            insufficientCells: 0,
            refusedCells: 0,
            candidateAreas: [],
            areasWithoutDirect: [],
            projectedSuppressedDomains: 0,
            cells: [],
          };
        }
        return previewAreaLevel(
          conversion.cell,
          disclosure,
          job.runConfig.confidence,
        );
      }
      return previewFeasibility({
        table: job.table,
        roles: job.roles,
        indicatorRules: job.indicatorRules,
        collapses: job.collapses,
        ...(job.populationCounts !== null
          ? { populationCounts: job.populationCounts }
          : {}),
        model: {
          transform: resolveTransform(job.runConfig.transform),
          confidence: job.runConfig.confidence,
          lonelyPsu: 'adjust',
          deffMethod: job.runConfig.deffBasis === 'pooled' ? 'pooled' : 'state',
        },
        disclosure,
      });
    },

    async run(job, onProgress) {
      if (job.path === 'area-level') {
        const conversion = tableToAreaLevelCell(
          job.table,
          areaLevelRolesOf(job),
          { estimateScale: job.estimateScale },
        );
        if (conversion.cell === null) {
          const first = conversion.issues.find((i) => i.severity === 'error');
          throw new Error(
            first?.message ??
              'The area-level table could not be converted for analysis.',
          );
        }
        return runAreaLevel({
          cell: conversion.cell,
          ioIssues: conversion.issues,
          covariateModes: job.covariateModes,
          disclosure: disclosureConfigFor(job.runConfig.disclosurePresetId),
          runConfig: job.runConfig,
          onProgress,
        });
      }
      const prepared = prepareMicrodataRun({
        table: job.table,
        roles: job.roles,
        indicatorRules: job.indicatorRules,
        collapses: job.collapses,
        subgroupsSelected: job.subgroupsSelected,
        covariateModes: job.covariateModes,
        companion: job.companion,
        runConfig: job.runConfig,
      });
      const result = await runAnalysis(prepared.input, prepared.config, onProgress);
      return {
        ...result,
        issues: [...prepared.logIssues, ...result.issues],
      };
    },
  };
}
